/**
 * WizardStepMetrics — krok 3/6 kreatora widgetu: agregacja/grupowanie (kpi/wykresy) albo
 * kolumny/sortowanie/limit (tabela). Reguły składania configu — patrz docstring
 * `WidgetCreatorWizard.tsx` i sekcja „Config assembly rules" w specyfikacji T1.7.
 */

import { useTranslation } from 'react-i18next';

import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { MultiSelect } from '@/components/MultiSelect';
import {
  WIDGET_AGGREGATE_FNS,
  WIDGET_DATE_BUCKETS,
  type WidgetAggregateFn,
  type WidgetDefinitionConfig,
  type WidgetSourceMeta,
  type WidgetVisualType,
} from '@/lib/widgets/types';

import { fieldKind, fieldLabel, isDateKindField } from './WidgetCreatorWizard';

const LABEL_CLASS = 'block text-sm font-medium text-text-secondary';
const MAX_TABLE_LIMIT = 50;
const DEFAULT_TABLE_LIMIT = 20;

export interface WizardStepMetricsProps {
  visualType: WidgetVisualType;
  config: WidgetDefinitionConfig;
  source: WidgetSourceMeta | undefined;
  patchConfig: (p: Partial<WidgetDefinitionConfig>) => void;
}

export function WizardStepMetrics({
  visualType,
  config,
  source,
  patchConfig,
}: WizardStepMetricsProps) {
  if (visualType === 'table') {
    return <TableMetrics config={config} source={source} patchConfig={patchConfig} />;
  }
  return (
    <AggregateMetrics
      visualType={visualType}
      config={config}
      source={source}
      patchConfig={patchConfig}
    />
  );
}

function AggregateMetrics({ visualType, config, source, patchConfig }: WizardStepMetricsProps) {
  const { t } = useTranslation();
  const agg = config.aggregate;
  const gb = config.groupBy;

  const allFieldOptions: DropdownOption[] = (source?.fields ?? []).map((f) => ({
    value: f.slug,
    label: f.label,
  }));
  const numericFieldOptions: DropdownOption[] = (source?.fields ?? [])
    .filter((f) => f.kind === 'numeric')
    .map((f) => ({ value: f.slug, label: f.label }));

  const needsField = agg?.fn !== 'count';
  const fieldOptions = agg?.fn === 'count_distinct' ? allFieldOptions : numericFieldOptions;

  function handleFnChange(nextFn: string) {
    const fn = nextFn as WidgetAggregateFn;
    const currentField = agg?.field;
    let nextField: string | undefined;
    if (fn === 'count') {
      nextField = undefined;
    } else if (fn === 'count_distinct') {
      nextField = currentField;
    } else {
      nextField = fieldKind(source, currentField) === 'numeric' ? currentField : undefined;
    }
    patchConfig({ aggregate: { fn, ...(nextField ? { field: nextField } : {}) } });
  }

  function handleAggFieldChange(nextField: string) {
    patchConfig({ aggregate: { fn: agg?.fn ?? 'count', field: nextField || undefined } });
  }

  function handleGroupByFieldChange(nextField: string) {
    const dateKind = isDateKindField(source, nextField);
    patchConfig({
      groupBy: {
        field: nextField,
        ...(dateKind ? { dateBucket: gb?.dateBucket ?? 'month' } : {}),
        ...(gb?.maxItems ? { maxItems: gb.maxItems } : {}),
      },
    });
  }

  function handleDateBucketChange(nextBucket: string) {
    if (!gb) return;
    patchConfig({
      groupBy: { ...gb, dateBucket: nextBucket as (typeof WIDGET_DATE_BUCKETS)[number] },
    });
  }

  function handleMaxItemsChange(raw: string) {
    if (!gb) return;
    const trimmed = raw.trim();
    if (trimmed === '') {
      const { maxItems: _maxItems, ...rest } = gb;
      patchConfig({ groupBy: rest });
      return;
    }
    const n = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(n)) return;
    patchConfig({ groupBy: { ...gb, maxItems: Math.max(1, Math.min(50, n)) } });
  }

  const groupByIsDate = isDateKindField(source, gb?.field);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className={LABEL_CLASS} htmlFor="widget-wizard-agg-fn">
          {t('tenant.widgets.wizard.metrics.aggregateFnLabel')}
        </label>
        <Dropdown
          id="widget-wizard-agg-fn"
          value={agg?.fn ?? 'count'}
          onChange={handleFnChange}
          options={WIDGET_AGGREGATE_FNS.map((fn) => ({
            value: fn,
            label: t(`tenant.widgets.wizard.aggregateFn.${fn}`),
          }))}
          data-testid="widget-wizard-agg-fn-select"
        />
      </div>

      {needsField ? (
        <div className="space-y-2">
          <label className={LABEL_CLASS} htmlFor="widget-wizard-agg-field">
            {t('tenant.widgets.wizard.metrics.aggregateFieldLabel')}
          </label>
          <Dropdown
            id="widget-wizard-agg-field"
            value={agg?.field ?? ''}
            onChange={handleAggFieldChange}
            placeholder={t('tenant.widgets.wizard.source.placeholder')}
            options={fieldOptions}
            data-testid="widget-wizard-agg-field-select"
          />
        </div>
      ) : (
        <p className="text-xs text-text-muted">
          {t('tenant.widgets.wizard.metrics.aggregateFieldNotNeeded')}
        </p>
      )}

      {visualType !== 'kpi' && (
        <>
          <div className="space-y-2">
            <label className={LABEL_CLASS} htmlFor="widget-wizard-groupby-field">
              {t('tenant.widgets.wizard.metrics.groupByFieldLabel')}
            </label>
            <Dropdown
              id="widget-wizard-groupby-field"
              value={gb?.field ?? ''}
              onChange={handleGroupByFieldChange}
              placeholder={t('tenant.widgets.wizard.source.placeholder')}
              options={allFieldOptions}
              data-testid="widget-wizard-groupby-field-select"
            />
          </div>

          {groupByIsDate && (
            <div className="space-y-2">
              <label className={LABEL_CLASS} htmlFor="widget-wizard-date-bucket">
                {t('tenant.widgets.wizard.metrics.dateBucketLabel')}
              </label>
              <Dropdown
                id="widget-wizard-date-bucket"
                value={gb?.dateBucket ?? 'month'}
                onChange={handleDateBucketChange}
                options={WIDGET_DATE_BUCKETS.map((b) => ({
                  value: b,
                  label: t(`tenant.widgets.wizard.dateBucket.${b}`),
                }))}
                data-testid="widget-wizard-date-bucket-select"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className={LABEL_CLASS} htmlFor="widget-wizard-max-items">
              {t('tenant.widgets.wizard.metrics.maxItemsLabel')}
            </label>
            <Input
              id="widget-wizard-max-items"
              type="number"
              min={1}
              max={50}
              placeholder={t('tenant.widgets.wizard.metrics.maxItemsPlaceholder')}
              value={gb?.maxItems ?? ''}
              onChange={(e) => handleMaxItemsChange(e.target.value)}
              disabled={!gb?.field}
            />
          </div>
        </>
      )}
    </div>
  );
}

function TableMetrics({
  config,
  source,
  patchConfig,
}: {
  config: WidgetDefinitionConfig;
  source: WidgetSourceMeta | undefined;
  patchConfig: (p: Partial<WidgetDefinitionConfig>) => void;
}) {
  const { t } = useTranslation();
  const table = config.table;
  const columns = table?.columns ?? [];
  const fieldOptions = (source?.fields ?? []).map((f) => ({ value: f.slug, label: f.label }));

  function handleColumnsChange(nextColumns: string[]) {
    const currentSort = table?.sort;
    const sortStillValid = currentSort && nextColumns.includes(currentSort.field);
    patchConfig({
      table: {
        columns: nextColumns,
        limit: table?.limit ?? DEFAULT_TABLE_LIMIT,
        ...(sortStillValid ? { sort: currentSort } : {}),
      },
    });
  }

  function handleSortFieldChange(nextField: string) {
    if (!nextField) {
      patchConfig({ table: { columns, limit: table?.limit ?? DEFAULT_TABLE_LIMIT } });
      return;
    }
    patchConfig({
      table: {
        columns,
        limit: table?.limit ?? DEFAULT_TABLE_LIMIT,
        sort: { field: nextField, direction: table?.sort?.direction ?? 'asc' },
      },
    });
  }

  function handleSortDirectionChange(direction: string) {
    if (!table?.sort) return;
    patchConfig({
      table: {
        ...table,
        sort: { field: table.sort.field, direction: direction as 'asc' | 'desc' },
      },
    });
  }

  function handleLimitChange(raw: string) {
    const n = Number.parseInt(raw, 10);
    const clamped = Number.isFinite(n)
      ? Math.min(MAX_TABLE_LIMIT, Math.max(1, n))
      : DEFAULT_TABLE_LIMIT;
    patchConfig({
      table: { columns, limit: clamped, ...(table?.sort ? { sort: table.sort } : {}) },
    });
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <span className={LABEL_CLASS}>{t('tenant.widgets.wizard.metrics.columnsLabel')}</span>
        <MultiSelect
          value={columns}
          onChange={handleColumnsChange}
          options={fieldOptions}
          data-testid="widget-wizard-columns"
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS} htmlFor="widget-wizard-sort-field">
          {t('tenant.widgets.wizard.metrics.sortFieldLabel')}
        </label>
        <Dropdown
          id="widget-wizard-sort-field"
          value={table?.sort?.field ?? ''}
          onChange={handleSortFieldChange}
          placeholder={t('tenant.widgets.wizard.metrics.sortFieldNone')}
          disabled={columns.length === 0}
          options={columns.map((c) => ({ value: c, label: fieldLabel(source, c) }))}
          data-testid="widget-wizard-sort-field-select"
        />
        {columns.length === 0 && (
          <p className="text-xs text-text-muted">
            {t('tenant.widgets.wizard.metrics.sortFieldNeedsColumns')}
          </p>
        )}
      </div>

      {table?.sort && (
        <div className="space-y-2">
          <label className={LABEL_CLASS} htmlFor="widget-wizard-sort-direction">
            {t('tenant.widgets.wizard.metrics.sortDirectionLabel')}
          </label>
          <Dropdown
            id="widget-wizard-sort-direction"
            value={table.sort.direction}
            onChange={handleSortDirectionChange}
            options={[
              { value: 'asc', label: t('tenant.widgets.wizard.metrics.sortDirection.asc') },
              { value: 'desc', label: t('tenant.widgets.wizard.metrics.sortDirection.desc') },
            ]}
            data-testid="widget-wizard-sort-direction-select"
          />
        </div>
      )}

      <div className="space-y-2">
        <label className={LABEL_CLASS} htmlFor="widget-wizard-limit">
          {t('tenant.widgets.wizard.metrics.limitLabel')}
        </label>
        <Input
          id="widget-wizard-limit"
          type="number"
          min={1}
          max={MAX_TABLE_LIMIT}
          value={table?.limit ?? DEFAULT_TABLE_LIMIT}
          onChange={(e) => handleLimitChange(e.target.value)}
        />
      </div>
    </div>
  );
}
